import { useAlertEmit } from "@fpsoftware/mvp";
import cn from "classnames";
import type { HTMLAttributes, ReactNode } from "react";
import type { Accept } from "react-dropzone";
import { useDropzone } from "react-dropzone";
import { TbLoader2 } from "react-icons/tb";
import styles from "./Dropzone.module.css";

interface DropzoneProps {
  id?: string;
  onFiles: (files: File[]) => void;
  accept?: Accept;
  multiple?: boolean;
  disabled?: boolean;
  loading?: boolean;
  hint?: string;
  children: ReactNode;
}

export function Dropzone(props: DropzoneProps) {
  const { id, onFiles, accept, multiple, disabled, loading, hint, children } =
    props;
  const emit = useAlertEmit();

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: (accepted, rejections) => {
      if (rejections.length > 0) {
        emit({
          type: "error",
          content: (
            <>
              <div className="mb-3">Unsupported files:</div>
              {rejections.map((r) => (
                <div style={{ marginBottom: ".25rem" }} key={r.file.name}>
                  {r.file.name}
                </div>
              ))}
            </>
          ),
        });
        return;
      }

      if (accepted.length > 0) {
        onFiles(accepted);
      }
    },
    accept,
    multiple,
    disabled: disabled || loading,
  });

  return (
    <div
      {...(getRootProps() as HTMLAttributes<HTMLDivElement>)}
      className={cn(styles.dropzone, {
        [styles.active]: isDragActive,
        [styles.disabled]: disabled || loading,
      })}
    >
      <input {...getInputProps({ id })} />
      {loading && (
        <div className={styles.overlay}>
          <TbLoader2 size={32} className={styles.spinner} />
        </div>
      )}
      {children}
      {hint && <span className={styles.hint}>{hint}</span>}
    </div>
  );
}
